import { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Chat from "./pages/Chat";
import {
  PokemonStatsPage,
  PokemonLeaguePage,
  PokemonLorePage,
  PokemonMovesPage,
  PokemonEvolutionPage,
  PokemonCandyPage,
  PokemonCountersPage,
  PokemonBestIvsPage,
  PokemonEliteTmPage,
  PokemonIvRankingPage,
  PokemonXlCandyPage,
  MoveOrComparisonPage,
  ComparisonPage,
  LeagueTopPage,
  SeasonSummaryPage,
  SeasonCupsPage,
  SeasonMoveChangesPage,
  SeasonNewMovesPage,
  SeasonCupRulesPage,
} from "./pages/cardRoutes";
import SnapshotPage from "./pages/SnapshotPage";
import { useAuthStore } from "./store/authStore";

/**
 * Top-level router.  Phase A of the shareable-card-URLs plan introduces
 * URL-driven views (`/p/<pokemon>`, `/m/<move>`, etc.) alongside the
 * chat experience served at `/`.  See docs/share-urls-plan.md §3 for
 * the full route table; A3 lands the first three.
 *
 * The catch-all `*` route still points at Chat to mirror the historical
 * Cloudflare Pages SPA fallback.  A6 replaces it with a proper 404
 * once all evergreen routes are in.
 */
export default function App() {
  const { initAuth } = useAuthStore();

  // Parse URL fragment for magic-link token on first mount
  useEffect(() => {
    initAuth();
  }, [initAuth]);

  return (
    <BrowserRouter>
      <div className="h-[100dvh] flex flex-col bg-scout-bg dark overflow-x-hidden">
        <Routes>
          <Route path="/" element={<Chat />} />
          {/* Phase A — shareable card URLs (evergreen, live-data) */}
          {/* Order matters in React Router v6: more-specific paths
              must be listed before /p/:pokemon/:league which would
              otherwise match /p/swampert/moves and route to the
              league handler.  Static-segment routes win because the
              compiled matcher prefers exact tokens over params. */}
          <Route path="/p/:pokemon" element={<PokemonStatsPage />} />
          <Route path="/p/:pokemon/moves" element={<PokemonMovesPage />} />
          <Route path="/p/:pokemon/evolution" element={<PokemonEvolutionPage />} />
          <Route path="/p/:pokemon/candy" element={<PokemonCandyPage />} />
          <Route path="/p/:pokemon/counters" element={<PokemonCountersPage />} />
          <Route path="/p/:pokemon/best-ivs" element={<PokemonBestIvsPage />} />
          <Route path="/p/:pokemon/elite-tm" element={<PokemonEliteTmPage />} />
          <Route path="/p/:pokemon/iv/:ivs" element={<PokemonIvRankingPage />} />
          <Route path="/p/:pokemon/xl/:league" element={<PokemonXlCandyPage />} />
          {/* Generic /p/:pokemon/:league handler — runs only when no
              static-segment route above matched.  Inside the component
              we still guard on isStandardLeague() because someone might
              hit /p/foo/some-cup-slug. */}
          <Route path="/p/:pokemon/:league" element={<PokemonLeaguePage />} />
          <Route path="/lore/:pokemon" element={<PokemonLorePage />} />
          {/* /m/:slug handles both move_detail and move_comparison —
              dispatch by slug content (presence of "-vs-") inside
              MoveOrComparisonPage. */}
          <Route path="/m/:slug" element={<MoveOrComparisonPage />} />
          <Route path="/compare/:names" element={<ComparisonPage />} />
          <Route path="/league/:league/top" element={<LeagueTopPage />} />
          {/* Phase B — season share URLs.
              Same static-segment-wins ordering rule as /p/:pokemon/* —
              /season/:slug/cups must be listed before the bare
              /season/:slug catch so the cups handler wins. */}
          <Route path="/season" element={<SeasonSummaryPage />} />
          <Route path="/season/:slug/cups" element={<SeasonCupsPage />} />
          <Route path="/season/:slug/move-changes" element={<SeasonMoveChangesPage />} />
          <Route path="/season/:slug/new-moves" element={<SeasonNewMovesPage />} />
          <Route path="/season/:slug/cup/:cupSlug" element={<SeasonCupRulesPage />} />
          <Route path="/season/:slug" element={<SeasonSummaryPage />} />
          {/* Phase C — snapshot share URLs (state-of-today intents) */}
          <Route path="/s/:token" element={<SnapshotPage />} />
          {/* Catch-all → Chat for now; A7 replaces with proper 404 */}
          <Route path="*" element={<Chat />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}
